import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  dashboardAPI,
  tripsAPI,
  trucksAPI,
  ordersAPI,
  invoicesAPI,
  stockMovementsAPI,
  exportAPI,
} from '../lib/api';
import { downloadBlob } from '../lib/download';
import { messageErreur } from '../lib/erreurs';
import { formatDate, formatLitres } from '../lib/formats';
import { libelle, referenceLisible } from '../lib/libelles';
import { toast } from '../components/ui/sonner';
import { fetchAllPages } from '../lib/fetchAllPages';
import { LEXIQUE } from '../lib/lexique';
import {
  ArrowRight,
  BarChart3,
  ChevronRight,
  Clock,
  DollarSign,
  Download,
  Droplets,
  Fuel,
  Layers,
  Loader2,
  Map,
  Plus,
  RefreshCw,
  Rocket,
  ShoppingCart,
  TrendingUp,
  Truck,
  Wallet,
  Wrench,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../components/ui/sheet';
import {
  cn,
  getStatusBadgeClass,
  formatRelativeTime,
  getTripProgress,
  formatCurrency,
  formatVolume,
  formatCompactNumber,
  formatCompactCurrency,
  DEFAULT_STOCK_THRESHOLDS,
  getStockAlert,
  jaugeStock,
} from '../lib/utils';
import { useSync } from '../context/SyncContext';
import { useAuth } from '../context/AuthContext';
import { LoadingState } from '../components/ui/loading-state';
import { EmptyState } from '../components/ui/empty-state';
import { Convoi } from '../components/ui/icons/domain-icons';
import { ONBOARDING_ROUTE } from '../components/onboarding';
import { estFactureOuverte, resteFacture, sommeMontants } from '../lib/montantsFacture';
import { useRafraichirSur } from '../hooks/useRafraichirSur';
import {
  WelcomeHero,
  AlerteATraiter,
  ACeJour,
  FuelGauge,
  SectionCard,
} from '../components/dashboard';
import {
  AutresActivites,
  construirePanneaux,
  domainesVisibles,
} from '../components/dashboard/AutresActivites';


const ActiveTripCard = ({ trip, truck, operation, onClick }) => {
  const progress = getTripProgress(trip.status);
  const isAtCustoms = ['AT_CUSTOMS_EXIT', 'AT_CUSTOMS_ENTRY'].includes(
    trip.status,
  );

  return (
    <Card
      className={cn(
        'card-base card-interactive',
        isAtCustoms && 'border-flame/50 border-2',
      )}
      onClick={onClick}
      data-testid={`trip-card-${trip.id}`}
    >
      <CardContent className="p-3">
        <div className="flex items-start justify-between mb-2">
          <div className="matricule-plate text-sm">
            {truck?.matricule || 'Camion non précisé'}
          </div>
          <Badge
            className={cn('status-badge', getStatusBadgeClass(trip.status))}
          >
            {libelle('tripStatus', trip.status)}
          </Badge>
        </div>

        {operation && referenceLisible(operation.code, '') && (
          <p className="text-xs font-mono text-flame font-bold mb-2">
            Convoi {referenceLisible(operation.code, '')}
          </p>
        )}

        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
          <span className="font-medium">{trip.departure_country}</span>
          <ArrowRight className="w-4 h-4" />
          <span className="font-medium">{trip.destination_country}</span>
        </div>

        <div className="trip-progress mb-2">
          <div
            className="trip-progress-bar"
            style={{ width: `${progress}%` }}
          />
        </div>

        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>{progress}% du trajet fait</span>
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {formatRelativeTime(trip.updated_at)}
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { triggerSync, isSyncing, isOnline } = useSync();
  const {
    user,
    activePack,
    enabledModules,
    hasModule,
    isAdminSociete,
    canAccessLogistics,
    canSeeMoney,
    managesDepot,
  } = useAuth();
  const [stats, setStats] = useState(null);
  const [activeTrips, setActiveTrips] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [operations] = useState([]);
  const [businessPilotage, setBusinessPilotage] = useState(null);
  const [recentAllocations, setRecentAllocations] = useState([]);
  const [urgentOrdersCount, setUrgentOrdersCount] = useState(0);
  const [dueSoonOrdersCount, setDueSoonOrdersCount] = useState(0);
  // Bandeau « À ce jour »: litres restant à livrer (commandes ouvertes) et
  // montant des factures pas encore encaissées. `null` = pas encore su.
  const [aLivrer, setALivrer] = useState(null);
  const [aEncaisser, setAEncaisser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showTruckDetails, setShowTruckDetails] = useState(false);
  const [selectedTruck, setSelectedTruck] = useState(null);
  const [truckStats, setTruckStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const handleExportExcel = useCallback(async () => {
    if (!isOnline) return;

    try {
      const response = await exportAPI.downloadExcel();
      downloadBlob(
        response,
        'I-CARBU-logistique-export.xlsx',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      );
    } catch (err) {
      console.error('Export Excel failed:', err);
      toast.error(messageErreur(err, 'L’export n’a pas pu être préparé. Réessayez.'));
    }
  }, [isOnline]);

  // Les autres domaines de la licence (stations, contrôle du carburant) ne
  // sont demandés au serveur que si le pack les contient et que l'utilisateur
  // y a droit: le pack Transport et distribution n'en a aucun.
  const shouldLoadAdditionalPilotage =
    domainesVisibles({ activePack, enabledModules }).length > 0;

  const loadData = useCallback(async ({ silencieux = false } = {}) => {
    try {
      if (!silencieux) setIsLoading(true);
      setError(null);

      const [
        statsData,
        tripsData,
        trucksPage,
        ordersPage,
        stockSummary,
        pilotageData,
      ] = await Promise.all([
        dashboardAPI.getStats(),
        tripsAPI.getActive(),
        // Tout le parc et toutes les commandes: les retards, les litres à
        // livrer et la plaque de chaque trajet ne se calculent pas sur les
        // deux cents premières lignes.
        fetchAllPages((params) => trucksAPI.getAllPaged(params)),
        fetchAllPages((params) => ordersAPI.getAllPaged(params)),
        stockMovementsAPI.getSummary(),
        shouldLoadAdditionalPilotage
          ? dashboardAPI.getBusinessCockpit().catch(() => null)
          : Promise.resolve(null),
      ]);

      const orders = ordersPage?.items || [];
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const doneStatuses = ['DELIVERED', 'CANCELLED', 'PAID', 'INVOICED'];

      const overdueUrgentCount = orders.filter((order) => {
        if (doneStatuses.includes(String(order.status || ''))) return false;

        const quantity = Number(order.quantity_liters || 0);
        const delivered = Number(order.delivered_quantity || 0);
        const explicitRemaining = Number(order.remaining_quantity);
        const remaining = Number.isFinite(explicitRemaining)
          ? explicitRemaining
          : Math.max(quantity - delivered, 0);
        if (remaining <= 0) return false;

        if (!order.requested_delivery_date) return false;
        const dueDate = new Date(order.requested_delivery_date);
        if (Number.isNaN(dueDate.getTime())) return false;
        dueDate.setHours(0, 0, 0, 0);

        return dueDate < today;
      }).length;

      const dueSoonCount = orders.filter((order) => {
        if (doneStatuses.includes(String(order.status || ''))) return false;

        const quantity = Number(order.quantity_liters || 0);
        const delivered = Number(order.delivered_quantity || 0);
        const explicitRemaining = Number(order.remaining_quantity);
        const remaining = Number.isFinite(explicitRemaining)
          ? explicitRemaining
          : Math.max(quantity - delivered, 0);
        if (remaining <= 0) return false;

        if (!order.requested_delivery_date) return false;
        const dueDate = new Date(order.requested_delivery_date);
        if (Number.isNaN(dueDate.getTime())) return false;
        dueDate.setHours(0, 0, 0, 0);

        const diffDays = Math.round(
          (dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
        );

        return diffDays >= 0 && diffDays <= 2;
      }).length;

      setUrgentOrdersCount(overdueUrgentCount);
      setDueSoonOrdersCount(dueSoonCount);

      // Reste à livrer: la répartition fournit `progress.to_deliver_liters`;
      // à défaut, litres commandés moins litres livrés.
      const ouvertes = orders.filter(
        (order) => !doneStatuses.includes(String(order.status || '')),
      );
      const litresALivrer = ouvertes.reduce((somme, order) => {
        const calcule = Number(order.progress?.to_deliver_liters);
        if (Number.isFinite(calcule)) return somme + Math.max(calcule, 0);
        const explicite = Number(order.remaining_quantity);
        const reste = Number.isFinite(explicite)
          ? explicite
          : Number(order.quantity_liters || 0) - Number(order.delivered_quantity || 0);
        return somme + Math.max(reste, 0);
      }, 0);
      const commandesALivrer = ouvertes.filter((order) => {
        const calcule = Number(order.progress?.to_deliver_liters);
        if (Number.isFinite(calcule)) return calcule > 0;
        const explicite = Number(order.remaining_quantity);
        const reste = Number.isFinite(explicite)
          ? explicite
          : Number(order.quantity_liters || 0) - Number(order.delivered_quantity || 0);
        return reste > 0;
      }).length;
      setALivrer({ litres: litresALivrer, commandes: commandesALivrer });

      // Use stock summary data from API
      const currentStockLiters = stockSummary?.current_stock_liters || 0;
      const { niveau: rollingLevel, capacite: capaciteSuivie } = jaugeStock(
        currentStockLiters,
        stockSummary?.imports,
      );
      const stockAlert = getStockAlert(
        currentStockLiters,
        DEFAULT_STOCK_THRESHOLDS,
      );

      const mergedStats = {
        ...(statsData || {}),
        stock: {
          ...(statsData?.stock || {}),
          current_liters: currentStockLiters,
          rolling_level: rollingLevel,
          capacite_suivie: capaciteSuivie,
          alert_level: stockAlert.level,
          alert_label: stockAlert.label,
          alert_message: stockAlert.message,
          thresholds: DEFAULT_STOCK_THRESHOLDS,
          // Sans dépôt: le chargé non livré, trajet par trajet (camion et
          // litres), tel que le serveur l'a compté pour le total.
          charges_par_trajet:
            stockSummary?.manages_depot === false && Array.isArray(stockSummary?.by_truck)
              ? stockSummary.by_truck
              : null,
        },
      };

      setStats(mergedStats);
      setActiveTrips(tripsData);
      setTrucks(trucksPage.items || []);
      setBusinessPilotage(pilotageData);

      const allocationRows = orders
        .flatMap((order) =>
          (order.allocated_trips || []).map((allocation, index) => {
            const allocationDate =
              allocation.allocated_at || order.updated_at || order.order_date;
            const unitPrice = Number(order.unit_price || 0);
            const totalAmount = Number(order.total_amount || 0);
            const totalQuantity = Number(order.quantity_liters || 0);
            const fallbackUnit =
              totalQuantity > 0 ? totalAmount / totalQuantity : 0;
            const valuePerLiter = unitPrice || fallbackUnit;
            const allocationQuantity = Number.isFinite(
              Number(allocation.quantity || 0),
            )
              ? Number(allocation.quantity || 0)
              : 0;
            const allocationValue = valuePerLiter * allocationQuantity;

            return {
              id: `${order.id}-${allocation.trip_id}-${index}`,
              reference: order.reference_number ? referenceLisible(order.reference_number, '') : '',
              quantity: allocationQuantity,
              value: allocationValue || totalAmount || 0,
              allocated_at: allocationDate,
            };
          }),
        )
        .filter((row) => row.allocated_at)
        .sort(
          (a, b) =>
            new Date(b.allocated_at).getTime() -
            new Date(a.allocated_at).getTime(),
        )
        .slice(0, 3);

      setRecentAllocations(allocationRows);
    } catch (err) {
      console.error('Failed to load dashboard:', err);
      setError(messageErreur(err, 'Impossible de charger le tableau de bord. Réessayez.'));
      setUrgentOrdersCount(0);
      setDueSoonOrdersCount(0);
      setBusinessPilotage(null);
    } finally {
      setIsLoading(false);
    }
  }, [shouldLoadAdditionalPilotage]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Toute écriture (paiement, trajet, dépense, autre onglet) relit l'accueil
  // sans écran de chargement: voir lib/fraicheur.js.
  const [versionFactures, setVersionFactures] = useState(0);
  useRafraichirSur(['accueil'], (options) => {
    setVersionFactures((v) => v + 1);
    return loadData(options);
  });

  // Les factures à encaisser se chargent à part: un accueil qui attend la liste
  // complète des factures pour afficher les camions serait plus lent pour rien,
  // et une licence sans facturation n'a pas à les demander. Même règle que la
  // page Factures: tout ce qui n'est ni payé ni annulé compte pour son solde.
  const peutVoirFactures = hasModule('invoices');
  useEffect(() => {
    if (!peutVoirFactures) {
      setAEncaisser(null);
      return undefined;
    }
    let actif = true;
    fetchAllPages((params) => invoicesAPI.getAllPaged(params))
      .then(({ items }) => {
        if (!actif) return;
        // Même définition que le serveur (`open_receivables_total`): les
        // brouillons ne sont pas des créances.
        const ouvertes = items.filter(estFactureOuverte);
        const montant = sommeMontants(ouvertes.map(resteFacture));
        setAEncaisser({ montant, factures: ouvertes.filter((f) => resteFacture(f) > 0).length });
      })
      .catch(() => {
        if (actif) setAEncaisser(null);
      });
    return () => {
      actif = false;
    };
  }, [peutVoirFactures, versionFactures]);

  const getTruckForTrip = (truckId) => {
    return trucks.find((t) => t.id === truckId);
  };

  const getOperationForTrip = (operationId) => {
    return operations.find((o) => o.id === operationId);
  };

  // Aperçu rapide d'un camion. Plus aucune recette calculée ici: une somme
  // de `total_amount` faite dans le navigateur ne correspondait à aucun
  // chiffre du serveur. Les comptes de trajets suffisent; le reste est sur
  // la fiche du camion.
  const openTruckDetails = async (truck) => {
    setSelectedTruck(truck);
    setShowTruckDetails(true);
    setStatsLoading(true);
    setTruckStats(null);

    try {
      const trips = await tripsAPI.getAll({ truck_id: truck.id });
      const rows = Array.isArray(trips) ? trips : [];
      setTruckStats({
        total_trips: rows.length,
        active_trips: rows.filter((t) => !['COMPLETED', 'CANCELLED'].includes(t.status)).length,
      });
    } catch (err) {
      console.error('Failed to load truck stats:', err);
    } finally {
      setStatsLoading(false);
    }
  };

  const customsTrips = activeTrips.filter((t) =>
    ['AT_CUSTOMS_EXIT', 'AT_CUSTOMS_ENTRY'].includes(t.status),
  );

  const distributionSource =
    stats?.stock?.distribution || stats?.distribution || null;
  const distributionValues = distributionSource || {};
  const distributionTotal = Object.values(distributionValues).reduce(
    (sum, value) => sum + (Number.isFinite(Number(value)) ? Number(value) : 0),
    0,
  );
  const getDistributionPercent = (value) =>
    distributionTotal > 0 ? (Number(value || 0) / distributionTotal) * 100 : 0;

  const formatAllocationDate = (value) => formatDate(value, '');

  const panneauxAutresActivites = construirePanneaux({
    pilotage: businessPilotage,
    activePack,
    enabledModules,
    canSeeMoney,
  });

  if (isLoading) {
    return (
      <div className="mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8">
        <LoadingState shape="readouts" />
      </div>
    );
  }

  const seuilBas = Number(
    stats?.stock?.thresholds?.low ?? DEFAULT_STOCK_THRESHOLDS.low,
  );
  const litresEnStock = Number(stats?.stock?.current_liters || 0);
  const capaciteSuivie = Number(
    stats?.stock?.capacite_suivie ?? jaugeStock(litresEnStock, 0).capacite,
  );
  // Ce qu'il manque pour repasser au-dessus du seuil bas. Zéro quand on est
  // au-dessus — et c'est bien zéro qu'il faut afficher, pas un tiret: « 0 L à
  // reconstituer » est une bonne nouvelle, une absence de chiffre n'est rien.
  const litresSousLeSeuil = Math.max(0, seuilBas - litresEnStock);
  // Sans dépôt: la même jauge mesure le remplissage des citernes des
  // camions chargés et pas encore livrés. Le chiffre du haut (le stock) et les
  // citernes du bas portent sur les mêmes camions: ceux que le stock compte,
  // trajet par trajet. Sans ce détail (ancienne réponse), les trajets actifs
  // chargés en tiennent lieu.
  const chargesStock = stats?.stock?.charges_par_trajet;
  const camionsCharges = Array.isArray(chargesStock)
    ? chargesStock.map((e) => ({ truck_id: e.truck_id, charge: Number(e.in_transit_liters || 0) }))
    : activeTrips
        .map((t) => ({
          truck_id: t.truck_id,
          charge: Number(t.fuel_quantity_real || t.allocated_quantity || 0),
        }))
        .filter((t) => t.charge > 0);
  // Une citerne porte au moins ce qu'elle a chargé, et l'ensemble au moins le
  // stock: la jauge ne dépasse jamais 100 %.
  const capaciteEnRoute = Math.max(
    litresEnStock,
    camionsCharges.reduce((total, t) => {
      const camion = trucks.find((c) => c.id === t.truck_id);
      const capacite = Number(camion?.capacity_liters || 0);
      return total + Math.max(capacite, t.charge);
    }, 0),
  );
  const niveauEnRoute = capaciteEnRoute > 0
    ? Math.min(100, (litresEnStock / capaciteEnRoute) * 100)
    : 0;
  const etatStock =
    stats?.stock?.alert_level === 'low'
      ? 'bas'
      : stats?.stock?.alert_level === 'high'
        ? 'haut'
        : 'normal';

  return (
    <div
      className="mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8"
      data-testid="dashboard-page"
    >
      {/* Ce qui demande une action: une icône animée dans le coin de
          l'en-tête, la liste dans un petit menu (plus de ligne pleine
          largeur « À TRAITER »). */}
      <WelcomeHero
        nomUtilisateur={user?.full_name || user?.name}
        aTraiter={
          <AlerteATraiter
            elements={[
              {
                id: 'retard',
                nombre: urgentOrdersCount,
                libelle: `${urgentOrdersCount} commande${urgentOrdersCount > 1 ? 's' : ''} en retard`,
                testid: 'quick-action-urgent-orders',
                onClick: () => navigate('/orders?urgency=OVERDUE'),
              },
              {
                id: 'echeance',
                nombre: dueSoonOrdersCount,
                libelle: `${dueSoonOrdersCount} échéance${dueSoonOrdersCount > 1 ? 's' : ''} sous 48 h`,
                testid: 'quick-action-due-soon-orders',
                onClick: () => navigate('/orders?urgency=DUE_SOON'),
              },
              {
                id: 'douane',
                nombre: customsTrips.length,
                libelle: `${customsTrips.length} camion${customsTrips.length > 1 ? 's' : ''} en douane`,
                testid: 'quick-action-customs-trips',
                onClick: () => navigate('/trips'),
              },
            ]}
          />
        }
      />

      {error && (
        <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-4 text-destructive">
          {error}
        </div>
      )}

      {/* Flotte vide: tous les instruments en dessous affichent zéro et
          n'apprennent rien. Ce qui manque n'est pas une explication, c'est le
          premier camion — et le chemin le plus court pour l'obtenir. */}
      {trucks.length === 0 && activeTrips.length === 0 && (
        <EmptyState
          icon={Rocket}
          title="Votre société n’a encore rien à piloter"
          description="Cet écran suit les camions en route, les commandes en retard et les volumes livrés. Il se remplit dès qu’un camion, un chauffeur et une commande existent."
          actionLabel={isAdminSociete ? 'Démarrer en 10 minutes' : undefined}
          onAction={() => navigate(ONBOARDING_ROUTE)}
          secondaryAction={
            isAdminSociete && hasModule('trucks') ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/trucks')}
              >
                Ajouter un camion
              </Button>
            ) : undefined
          }
          data-testid="dashboard-first-day"
        />
      )}

      {/* Bandeau « À ce jour »: six chiffres au plus dans l'ordre du parcours —
          le camion est libre, il roule, il est à l'atelier, le carburant est à
          livrer, l'argent est à encaisser, les convois avancent. Une ligne en
          grand écran, deux en moyen. Chaque tuile mène à l'écran où l'on agit. */}
      <ACeJour
        titre={LEXIQUE.aCeJour}
        data-testid="a-ce-jour"
        tuiles={[
          {
            id: 'fleet-tile-free',
            icone: Truck,
            animation: 'respire',
            libelle: 'Disponibles',
            libelleComplet: LEXIQUE.camionsDisponibles,
            valeur: stats ? Number(stats?.trucks?.free || 0) : null,
            sousLigne: `sur ${stats?.trucks?.total || 0} camion${(stats?.trucks?.total || 0) > 1 ? 's' : ''}`,
            vide: LEXIQUE.aucunCamionDisponible,
            proportion: stats?.trucks?.total
              ? (stats.trucks.free || 0) / stats.trucks.total
              : null,
            ton: 'disponible',
            onClick: () => navigate('/trucks?status=FREE'),
          },
          {
            id: 'fleet-tile-on-trip',
            icone: Truck,
            animation: 'roule',
            libelle: LEXIQUE.enRoute,
            valeur: stats ? Number(stats?.trucks?.on_trip || 0) : null,
            sousLigne:
              stats?.trips?.at_customs > 0
                ? `dont ${stats.trips.at_customs} à la douane`
                : 'camions sur la route',
            vide: LEXIQUE.aucunCamionEnRoute,
            proportion: stats?.trucks?.total
              ? (stats.trucks.on_trip || 0) / stats.trucks.total
              : null,
            ton: 'route',
            onClick: () => navigate('/trips'),
          },
          {
            id: 'fleet-tile-maintenance',
            icone: Wrench,
            animation: 'oscille',
            libelle: 'En entretien',
            valeur: stats ? Number(stats?.trucks?.maintenance || 0) : null,
            sousLigne: 'camions à l’atelier',
            vide: 'Aucun camion à l’atelier',
            proportion: stats?.trucks?.total
              ? (stats.trucks.maintenance || 0) / stats.trucks.total
              : null,
            ton: stats?.trucks?.maintenance ? 'critique' : 'atelier',
            onClick: () => navigate('/trucks?status=MAINTENANCE'),
          },
          {
            id: 'tile-to-deliver',
            icone: Droplets,
            animation: 'goutte',
            libelle: LEXIQUE.aLivrer,
            valeur: aLivrer ? Number(aLivrer.litres || 0) : null,
            formater: (n) => `${formatCompactNumber(Math.round(n))} L`,
            sousLigne: aLivrer
              ? `${aLivrer.commandes} commande${aLivrer.commandes > 1 ? 's' : ''}`
              : null,
            vide: LEXIQUE.rienALivrer,
            ton: 'neutre',
            onClick: () => navigate('/orders'),
          },
          peutVoirFactures && {
            id: 'tile-to-collect',
            icone: Wallet,
            animation: 'brille',
            libelle: LEXIQUE.aEncaisser,
            valeur: aEncaisser ? Number(aEncaisser.montant || 0) : null,
            formater: (n) => formatCompactCurrency(Math.round(n)),
            sousLigne: aEncaisser
              ? `${aEncaisser.factures} facture${aEncaisser.factures > 1 ? 's' : ''} impayée${aEncaisser.factures > 1 ? 's' : ''}`
              : null,
            vide: LEXIQUE.rienAEncaisser,
            ton: aEncaisser?.montant > 0 ? 'atelier' : 'neutre',
            onClick: () => navigate('/invoices'),
          },
          {
            id: 'fleet-tile-operations',
            icone: Layers,
            libelle: 'Convois',
            libelleComplet: 'Convois en cours',
            valeur: stats ? Number(stats?.operations?.active || 0) : null,
            sousLigne: 'en cours',
            vide: 'Aucun convoi en cours',
            ton: 'neutre',
            onClick: () => navigate('/operations'),
          },
        ]}
      />

      {/* Actions rapides. */}
      <div className="rounded-2xl border border-border/70 bg-card p-4 shadow-sm md:p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0">
            <h2 className="text-base font-bold text-foreground">
              Actions rapides
            </h2>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              className="btn-primary rounded-xl"
              onClick={() => navigate('/trips/new')}
              data-testid="quick-action-new-trip"
            >
              <Plus className="mr-2 h-4 w-4" />
              Nouveau trajet
            </Button>
            <Button
              variant="outline"
              className="rounded-xl"
              onClick={() => navigate('/orders')}
              data-testid="quick-action-orders"
            >
              <ShoppingCart className="mr-2 h-4 w-4" />
              Commandes
            </Button>
            <Button
              variant="outline"
              className="rounded-xl"
              onClick={() => navigate('/stock')}
              data-testid="quick-action-stock"
            >
              <Fuel className="mr-2 h-4 w-4" />
              Stock
            </Button>
            <Button
              variant="outline"
              className="rounded-xl"
              onClick={() => navigate('/tracking')}
              data-testid="quick-action-map"
            >
              <Map className="mr-2 h-4 w-4" />
              Voir la carte
            </Button>

            {/* Exporter et actualiser agissent sur cet écran-ci, les quatre
                boutons précédents mènent ailleurs. Le trait dit lequel des deux
                on s'apprête à faire. */}
            <span
              className="mx-1 hidden h-6 w-px bg-border sm:block"
              aria-hidden="true"
            />
            {/* Le serveur refuse l'export aux autres rôles (403): le bouton
                n'est montré qu'à ceux qui peuvent s'en servir. */}
            {canAccessLogistics && (
              <Button
                variant="outline"
                className="rounded-xl"
                onClick={handleExportExcel}
                disabled={!isOnline}
                data-testid="export-excel-btn"
              >
                <Download className="mr-2 h-4 w-4" />
                <span className="hidden sm:inline">Exporter Excel</span>
                <span className="sm:hidden">Exporter</span>
              </Button>
            )}
            <Button
              variant="outline"
              size="icon"
              className="rounded-xl"
              onClick={() => {
                triggerSync();
                loadData();
              }}
              disabled={isSyncing}
              aria-label="Actualiser"
              data-testid="refresh-btn"
            >
              <RefreshCw className={cn('h-4 w-4', isSyncing && 'animate-spin')} />
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Niveau de carburant: même carte et même jauge avec ou sans dépôt;
            sans dépôt, les textes parlent du carburant chargé non livré. */}
        <SectionCard
          icon={Fuel}
          title="Niveau de carburant"
          actionLabel="Voir le détail"
          onAction={() => navigate('/stock')}
          data-testid="fuel-level-card"
        >
          {/* La mise en page suit la largeur de la carte (requête de
              conteneur, voir .fuel-card-*): jauge au-dessus des tuiles quand
              la carte est étroite, côte à côte seulement s'il y a la place. */}
          <div className="fuel-card-body">
            <div className="fuel-card-layout">
              <FuelGauge
                className="shrink-0 !w-[min(168px,100%)] sm:!w-[168px]"
                level={managesDepot ? Number(stats?.stock?.rolling_level || 0) : niveauEnRoute}
                etat={managesDepot ? etatStock : 'normal'}
                legende={managesDepot ? 'de la capacité suivie' : 'des citernes en route remplies'}
                valeurLegende={`${formatVolume(litresEnStock)} / ${formatVolume(managesDepot ? capaciteSuivie : capaciteEnRoute)}`.replace(/ L/g, '\u00a0L')}
              />

              <div className="fuel-card-tiles">
                <div className="fuel-tile rounded-xl border border-border/70 bg-muted/40 px-3 py-2.5">
                  <div className="flex min-w-0 items-center gap-2">
                    <Fuel className="h-4 w-4 shrink-0 text-flame" />
                    <span className="fuel-tile-label text-xs font-semibold text-muted-foreground">
                      {managesDepot ? 'Stock actuel' : 'Chargé, non livré'}
                    </span>
                  </div>
                  <p className="kpi-value fuel-tile-value mt-1 text-foreground">
                    {formatVolume(litresEnStock)}
                  </p>
                </div>

                <div className="fuel-tile rounded-xl border border-border/70 bg-muted/40 px-3 py-2.5">
                  <div className="flex min-w-0 items-center gap-2">
                    <TrendingUp
                      className={cn(
                        'h-4 w-4 shrink-0',
                        managesDepot && litresSousLeSeuil > 0
                          ? 'text-destructive'
                          : 'text-success',
                      )}
                    />
                    <span className="fuel-tile-label text-xs font-semibold text-muted-foreground">
                      {managesDepot ? 'À reconstituer' : 'Camions en route'}
                    </span>
                  </div>
                  <p
                    className={cn(
                      'kpi-value fuel-tile-value mt-1',
                      managesDepot && litresSousLeSeuil > 0
                        ? 'text-destructive'
                        : 'text-foreground',
                    )}
                  >
                    {managesDepot ? formatVolume(litresSousLeSeuil) : camionsCharges.length}
                  </p>
                  <p className="mt-1 whitespace-nowrap text-[0.7rem] leading-tight tabular-nums text-muted-foreground">
                    {managesDepot
                      ? `Seuil bas ${formatVolume(seuilBas)}`
                      : `Citernes : ${formatVolume(capaciteEnRoute)}`}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-border/70 pt-3">
            <Badge
              className={cn(
                'status-badge',
                managesDepot && etatStock === 'bas'
                  ? 'bg-destructive/15 text-destructive'
                  : managesDepot && etatStock === 'haut'
                    ? 'bg-flame/15 text-flame-ink'
                    : 'bg-success/15 text-success',
              )}
            >
              {managesDepot ? (stats?.stock?.alert_label || 'Stock normal') : 'En route'}
            </Badge>
            <p className="min-w-0 flex-1 text-right text-xs text-muted-foreground">
              {managesDepot
                ? (stats?.stock?.alert_message || 'Niveau de stock stable')
                : 'Sort du stock à la livraison'}
            </p>
          </div>
        </SectionCard>

        {/* Distribution. */}
        <SectionCard
          icon={BarChart3}
          title="Distribution"
          subtitle="Importation, vente et usage interne"
          actionLabel="Voir commandes"
          onAction={() => navigate('/orders')}
          className="lg:col-span-2"
          data-testid="distribution-card"
        >
          {distributionTotal <= 0 && (
            <p className="rounded-xl border border-dashed border-border bg-muted/40 px-3 py-4 text-center text-xs text-muted-foreground">
              La répartition apparaîtra dès les premières opérations.
            </p>
          )}
          <div className="flex h-3 overflow-hidden rounded-full border border-border bg-muted">
            <div
              className="h-full bg-accent-info"
              style={{
                width: `${getDistributionPercent(distributionValues.import)}%`,
              }}
            />
            <div
              className="h-full bg-accent-success"
              style={{
                width: `${getDistributionPercent(distributionValues.sale)}%`,
              }}
            />
            <div
              className="h-full bg-accent-warning"
              style={{
                width: `${getDistributionPercent(distributionValues.internal)}%`,
              }}
            />
          </div>

          <div className="mt-3 grid grid-cols-1 gap-2 text-xs text-muted-foreground sm:grid-cols-3 sm:gap-3">
            {[
              {
                cle: 'import',
                libelle: 'Importation',
                pastille: 'bg-accent-info',
                valeur: distributionValues.import,
              },
              {
                cle: 'sale',
                libelle: 'Vente',
                pastille: 'bg-accent-success',
                valeur: distributionValues.sale,
              },
              {
                cle: 'internal',
                libelle: 'Usage interne',
                pastille: 'bg-accent-warning',
                valeur: distributionValues.internal,
              },
            ].map((flux) => (
              <div key={flux.cle} className="flex items-center gap-2">
                <span className={cn('h-2 w-2 rounded-full', flux.pastille)} />
                {flux.libelle}
                <span className="ml-auto font-semibold tabular-nums text-foreground">
                  {Math.round(getDistributionPercent(flux.valeur))}%
                </span>
              </div>
            ))}
          </div>

          <div className="mt-4 border-t border-border/70 pt-4">
            <div className="mb-3 flex items-center justify-between">
              <p className="readout-label">Dernières répartitions</p>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs text-primary"
                onClick={() => navigate('/orders')}
              >
                Voir tout
              </Button>
            </div>
            {recentAllocations.length === 0 ? (
              <div className="rounded-xl border border-dashed border-border bg-muted/40 px-3 py-4 text-center text-xs text-muted-foreground">
                Aucune répartition récente
              </div>
            ) : (
              <div className="space-y-2">
                {recentAllocations.map((allocation) => (
                  <div
                    key={allocation.id}
                    className="flex items-center justify-between gap-3 rounded-xl border border-border/70 bg-muted/30 px-3 py-2"
                  >
                    <div className="flex min-w-0 flex-col">
                      <span className="truncate text-sm font-semibold text-foreground">
                        {allocation.reference ? `Commande ${allocation.reference}` : 'Commande sans référence'}
                      </span>
                      <span className="truncate text-xs text-muted-foreground">
                        {formatLitres(allocation.quantity)} ·{' '}
                        {formatAllocationDate(allocation.allocated_at)}
                      </span>
                    </div>
                    <div className="shrink-0 text-right">
                      <div className="text-sm font-bold tabular-nums text-foreground">
                        {formatCurrency(allocation.value)}
                      </div>
                      <div className="text-[10px] text-muted-foreground">
                        Valeur
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </SectionCard>
      </div>

      {stats?.financial?.total_completed > 0 && (
        <div className="flex items-center gap-4 rounded-2xl border border-border/70 bg-card p-4 shadow-sm">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-success/10 text-success">
            <DollarSign className="h-[22px] w-[22px]" />
          </span>
          <div className="min-w-0">
            <p className="readout-label">CA trajets terminés</p>
            <p className="kpi-value text-foreground">
              {formatCurrency(stats.financial.total_completed)}
            </p>
          </div>
        </div>
      )}

      {/* Trajets en cours. */}
      <SectionCard
        icon={Truck}
        title="Trajets en cours"
        actionLabel="Voir tous"
        onAction={() => navigate('/trips')}
        data-testid="active-trips-card"
      >
        {activeTrips.length === 0 ? (
          <div className="flex flex-col items-center rounded-xl border border-dashed border-border bg-muted/30 px-4 py-8 text-center">
            <Truck className="h-10 w-10 text-muted-foreground/40" />
            <p className="mt-3 text-sm font-semibold text-foreground">
              Aucun trajet en cours
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Vos trajets apparaîtront ici dès leur départ.
            </p>
            <Button
              className="btn-primary mt-4 rounded-xl"
              onClick={() => navigate('/trips/new')}
              data-testid="create-trip-btn"
            >
              <Plus className="mr-2 h-4 w-4" />
              Créer un trajet
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {activeTrips.slice(0, 6).map((trip) => (
              <ActiveTripCard
                key={trip.id}
                trip={trip}
                truck={getTruckForTrip(trip.truck_id)}
                operation={getOperationForTrip(trip.operation_id)}
                onClick={() => navigate(`/trips/${trip.id}`)}
              />
            ))}
          </div>
        )}
      </SectionCard>

      {/* Parc par statut — on liste, on ne recompte pas: le chiffre vit dans
          les tuiles du haut. */}
      <SectionCard
        icon={Convoi}
        title="Parc par statut"
        actionLabel="Gérer le parc"
        onAction={() => navigate('/trucks')}
        data-testid="fleet-by-status-card"
      >
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          {[
            { statut: 'FREE', teinte: 'disponible', icone: Fuel },
            { statut: 'ON_TRIP', teinte: 'route', icone: Truck },
            { statut: 'MAINTENANCE', teinte: 'atelier', icone: Wrench },
          ].map(({ statut, teinte, icone: Icone }) => {
            const total = stats?.trucks?.[statut.toLowerCase()] || 0;
            const camions = trucks.filter((t) => t.current_status === statut);

            return (
              <div
                key={statut}
                className="rounded-xl border border-border/70 bg-muted/20 p-3"
              >
                <button
                  type="button"
                  onClick={() => navigate(`/trucks?status=${statut}`)}
                  className="group flex w-full items-center gap-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <span
                    className={cn(
                      'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl',
                      teinte === 'disponible'
                        ? 'bg-success/10 text-success'
                        : teinte === 'route'
                          ? 'bg-[hsl(var(--cat-route))]/10 text-[hsl(var(--cat-route))]'
                          : 'bg-flame/10 text-flame-ink',
                    )}
                  >
                    <Icone className="h-5 w-5" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold text-muted-foreground">
                      {libelle('truckStatus', statut)}
                    </span>
                    <span className="kpi-value block text-foreground">
                      {total}
                    </span>
                  </span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/50 transition-transform duration-200 group-hover:translate-x-0.5" />
                </button>

                {camions.length === 0 ? (
                  <p className="mt-3 text-xs text-muted-foreground">
                    Aucun camion
                  </p>
                ) : (
                  <div className="mt-3 space-y-1.5">
                    {camions.slice(0, 3).map((camion) => (
                      <button
                        key={camion.id}
                        type="button"
                        onClick={() => openTruckDetails(camion)}
                        className="flex w-full items-center justify-between gap-2 rounded-lg bg-card px-2.5 py-1.5 text-left hover:bg-muted"
                      >
                        <span className="truncate font-mono text-sm font-bold">
                          {camion.matricule}
                        </span>
                        <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                          {formatLitres(camion.capacity_liters)}
                        </span>
                      </button>
                    ))}
                    {camions.length > 3 && (
                      <p className="text-center text-xs text-muted-foreground">
                        +{camions.length - 3} {camions.length - 3 > 1 ? 'autres' : 'autre'}
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </SectionCard>

      <AutresActivites panneaux={panneauxAutresActivites} onNavigate={navigate} />

      {/* Détail d'un camion. */}
      <Sheet open={showTruckDetails} onOpenChange={setShowTruckDetails}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-md">
          <SheetHeader>
            <SheetTitle>Détails du camion</SheetTitle>
          </SheetHeader>
          {selectedTruck && (
            <div className="mt-4 space-y-4">
              <div>
                <p className="text-sm text-muted-foreground">Matricule</p>
                <p className="text-lg font-bold">{selectedTruck.matricule}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Statut actuel</p>
                <Badge className="status-badge">
                  {libelle('truckStatus', selectedTruck.current_status)}
                </Badge>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Capacité</p>
                <p className="text-lg">
                  {formatLitres(selectedTruck.capacity_liters)}
                </p>
              </div>
              {statsLoading ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground/70" />
                </div>
              ) : truckStats ? (
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">
                      Total trajets
                    </span>
                    <span className="font-medium">
                      {truckStats.total_trips}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">
                      Trajets en cours
                    </span>
                    <span className="font-medium">
                      {truckStats.active_trips}
                    </span>
                  </div>
                </div>
              ) : null}
              <Button
                className="btn-primary w-full"
                onClick={() => {
                  setShowTruckDetails(false);
                  navigate(`/trucks/${selectedTruck.id}`, {
                    state: { from: window.location.pathname || '/' },
                  });
                }}
              >
                Voir le camion complet
              </Button>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default Dashboard;
